<script setup>
import { computed, ref, reactive } from 'vue';
import { useRouter } from 'vue-router';
import AddCustomer from './AddCustomer.vue';
import EditSubscription from './EditSubscription.vue';
import { useAppStore } from '../store/app';
import Loader from './Loader.vue';
// import { returndate } from '../common/methods';
import { format } from 'date-fns';

const props = defineProps({
  customers: {
    type: Array,
    required: true,
  },
  rowAction: {
    type: Function,
    required: true,
  },
  selectable: {
    type: Boolean,
  },
});

const store = useAppStore();
const router = useRouter();
const searchTxt = ref('');
const showEditSub = ref(false);
const tableHeaders = computed(() => {
  let headers = [
    {
      title: 'User Name',
      key: 'username',
    },

    {
      title: 'Email',
      key: 'email',
    },
    {
      title: 'Phone Number',
      key: 'phoneNumber',
    },
    {
      title: 'Name',
      key: 'name',
    },
    {
      title: 'Member Id',
      key: 'memberId',
    },
    {
      title: 'Notes',
      key: 'notes',
    },
    {
      title: 'Last Login Time',
      key: 'lastLoginTime',
    },
    {
      title: 'Actions',
      key: 'actions',
    },
  ];
  if (props.selectable) {
    headers.unshift({
      title: '',
      key: 'checkbox',
    });
  }
  return headers;
});

/**
 * Toggle Customer Edit Modal
 */
const onToggleCustomerEditModal = () =>
  (showNewCustomerDialog.value = !showNewCustomerDialog.value);

/**
 * Get customer's subscriptions
 */
const onCustomerSubscriptionsData = (customer) => {
  store.getCustomerSubscriptions({ customer });
  router.push('/customer-subscriptions');
};

/**
 * Set customer edit data
 */
const onEditCustomerData = (customer) => {
  customerObj.value = { ...customer, isNew: false };

  onToggleCustomerEditModal();
};

/**
 * Delete current customer
 */
// const onDeleteCurrentCustomer = ({ customer }) => {
//   const message = `Are you sure you want to delete ${customer?.firstName} ${customer?.lastName}`;
//   const isDelete = confirm(message);
//   if (isDelete) {
//     const customerId = customer?.id ?? customer?.customerId;
//     store.deleteCurrentCustomer({ customerId });
//   }
// };

const filteredCustomers = computed(() => {
  let customers = props.customers.filter((c) => {
    return filterVal.value == 'Subscribed'
      ? c.numberSubscriptions > 0
      : filterVal.value == 'No Subs'
      ? !c.numberSubscriptions
      : true;
  });
  if (!searchTxt.value) return customers;

  return customers.filter((customer) => {
    return (
      customer.firstName?.toLowerCase().includes(searchTxt.value.toLowerCase()) ||
      customer.lastName?.toLowerCase().includes(searchTxt.value.toLowerCase()) ||
      customer.email?.toLowerCase().includes(searchTxt.value.toLowerCase()) ||
      customer.username?.toLowerCase().includes(searchTxt.value.toLowerCase()) ||
      customer.memberId?.toString()?.toLowerCase().includes(searchTxt.value.toLowerCase())
    );
  });
});

const showNewCustomerDialog = ref(false);

const customerObj = ref({
  memberId: 0,
  firstName: '',
  lastName: '',
  username: '',
  email: '',
  phoneNumber: '',
  address: '',
  isNew: true,
  notes: '',
});

const onNewCustomerClick = () => {
  customerObj.value = {
    memberId: 0,
    firstName: '',
    lastName: '',
    username: '',
    email: '',
    phoneNumber: '',
    address: '',
    isNew: true,
    notes: '',
  };
  onToggleCustomerEditModal();
};

const onAddEditCustomerClose = (data) => {
  msgToUser.success = data?.success;
  msgToUser.message = data?.message;
  showNewCustomerDialog.value = false;
  store.fetchAllUsers();
  msgToUser.show = data?.message ? true : false;
};

const onEditSubClose = (data) => {
  msgToUser.success = data?.success;
  msgToUser.message = data?.message;
  showEditSub.value = false;
  msgToUser.show = data?.message ? true : false;
};

const msgToUser = reactive({
  show: false,
  message: '',
  success: false,
});
const initCustomerData = ref({
  status: 1,
  subscriptionTypeName: 'ONE_OFF',
  paymentType: 'staff',
});

const editSubObj = ref({
  isNew: true,
  customer: initCustomerData.value,
});
const filterVal = ref('All');
</script>

<template>
  <div>
    <v-card class="pa-8">
      <v-card-title>
        <v-row class="mb-2">
          <v-col>
            <span>{{ customers.length }} Customers</span>
            <v-btn
              class="ml-4 ma-2"
              color="primary"
              prepend-icon="mdi-account-plus"
              @click="onNewCustomerClick">
              New Customer
            </v-btn>
            <v-btn
              class="ml-4 ma-2"
              color="primary"
              prepend-icon="mdi-heart-plus"
              @click="
                ((editSubObj.isNew = true), (editSubObj.customer = initCustomerData)),
                  (showEditSub = true) // Set to default
              ">
              New Subscription
            </v-btn>
          </v-col>
          <v-spacer></v-spacer>
          <v-col cols="2">
            <v-select
              v-model="filterVal"
              variant="outlined"
              :items="['All', 'No Subs', 'Subscribed']"
              label="View By"
              density="compact"
              hide-details></v-select>
          </v-col>
          <v-col cols="3">
            <v-text-field
              density="compact"
              append-inner-icon="mdi-magnify"
              placeholder="Search by username/memberid/email..."
              single-line
              hide-details
              rounded
              v-model="searchTxt"
              variant="outlined"></v-text-field>
          </v-col>
        </v-row>
      </v-card-title>
      <v-card-text>
        <Loader></Loader>
        <v-data-table
          :sort-by="[{ key: 'lastLoginTime', order: 'desc' }]"
          :headers="tableHeaders"
          :items="filteredCustomers"
          :loading="store.appLoading"
          item-value="email"
          hover>
          <template v-slot:[`item.name`]="{ item }">
            {{ item.firstName }} {{ item.lastName }}
          </template>
          <template v-slot:[`item.notes`]="{ item }">
            {{ item.customerNotes }}
          </template>
          <template v-slot:[`item.lastLoginTime`]="{ item }">
            {{
              new Date(item.lastLoginTime).getTime() > 0
                ? format(new Date(item.lastLoginTime), 'dd/MM/yyyy HH:mm:ss')
                : 'N/A'
            }}
          </template>
          <template v-slot:[`item.actions`]="{ item }">
            <div class="d-flex align-center">
              <v-tooltip
                :text="
                  item.numberSubscriptions
                    ? `View Customer's Subscriptions`
                    : 'No Subs,click to create new sub'
                "
                location="bottom">
                <template #activator="{ props }">
                  <v-btn v-bind="props" @click="onCustomerSubscriptionsData(item)" variant="text">
                    <v-badge
                      v-if="item.numberSubscriptions"
                      :content="item.numberSubscriptions"
                      size="x-small"
                      offset-x="-5"
                      color="success">
                      <v-icon color="red" size="x-large">mdi-heart-outline</v-icon>
                    </v-badge>
                    <v-icon v-else color="grey" size="x-large">mdi-heart-broken-outline</v-icon>
                  </v-btn>
                </template>
              </v-tooltip>

              <v-tooltip text="Edit Customer Details" location="bottom">
                <template #activator="{ props }">
                  <v-btn
                    v-bind="props"
                    @click="onEditCustomerData(item)"
                    variant="text"
                    icon="mdi-pencil-outline">
                  </v-btn>
                </template>
              </v-tooltip>
            </div>
          </template>
        </v-data-table>
      </v-card-text>
    </v-card>
  </div>
  <v-snackbar
    location="top center"
    v-model="msgToUser.show"
    :timeout="2000"
    :color="msgToUser.success ? 'success' : 'error'">
    {{ msgToUser.message }}
  </v-snackbar>
  <AddCustomer
    v-if="showNewCustomerDialog"
    @on-close="onAddEditCustomerClose"
    :customer="customerObj"></AddCustomer>
  <EditSubscription
    v-if="showEditSub"
    @on-close="onEditSubClose"
    :data="editSubObj"
    :callback="() => {}" />
</template>

<style lang="scss"></style>
